import React, { useState, useMemo } from "react";

import {
  AppIcon as App,
  Code,
  Highlightable,
  WorkerIcon as Worker
} from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import * as Accordion from "ds/Accordion";
import Search from "ds/Search";
import { Tooltip } from "ds/Tooltip";

import * as S from "./Sections.styles";

type WithWorkers = {
  workerName: string;
  instances: { cliCommand: string; instance?: any }[];
};

type WithoutWorkers = { cliCommand: string; instance?: any };

type Props = {
  sortedCLICommands: {
    appName: string;
    withWorkers: WithWorkers[];
    withoutWorkers?: WithoutWorkers[];
  }[];
  searchTitle?: string;
  setSearchTitle: (arg: string) => void;
  commandsLength: number;
};

const CLISection = ({
  sortedCLICommands,
  searchTitle,
  setSearchTitle,
  commandsLength
}: Props) => {
  const intl = useIntl();
  const [showAllWorkerInstances, setShowAllWorkerInstances] = useState(false);
  const [showAllNoWorkerInstances, setShowAllNoWorkerInstances] =
    useState(false);

  const filterCLICommands = useMemo(() => {
    const apps = sortedCLICommands.filter(
      appLevel => searchTitle && appLevel.appName.includes(searchTitle)
    );
    if (apps.length) {
      return apps;
    } else {
      const apps = sortedCLICommands.filter(appLevel =>
        appLevel.withWorkers.filter(worker => {
          return searchTitle && worker.workerName.includes(searchTitle);
        })
      );
      const workers = apps.map(app => {
        const wrks = app.withWorkers.filter(worker => {
          return searchTitle && worker.workerName.includes(searchTitle);
        });

        return { appName: app.appName, withWorkers: wrks };
      });

      return workers;
    }
  }, [searchTitle, sortedCLICommands]);

  const CLICommands: {
    appName: string;
    withWorkers: WithWorkers[];
    withoutWorkers?: WithoutWorkers[];
  }[] = searchTitle ? filterCLICommands : sortedCLICommands;
  const shouldShowEmpty = !CLICommands.length;

  return (
    <div>
      {commandsLength > 10 && (
        <Search
          id="cli-commands"
          onChange={(value: string) => {
            setSearchTitle(value);
          }}
          placeholder={intl.formatMessage({ id: "search_apps" })}
          value={searchTitle}
        />
      )}
      {shouldShowEmpty ? (
        <S.NoResultWrapper>
          {intl.formatMessage({ id: "no_results" })}
        </S.NoResultWrapper>
      ) : (
        CLICommands.map((item, index) => {
          let appItemsLength =
            sortedCLICommands[index]?.withoutWorkers?.length || 0;
          sortedCLICommands[index].withWorkers?.map(
            worker => (appItemsLength += worker.instances.length)
          );
          const itemsWithoutWorkers =
            Number(item?.withoutWorkers?.length) > 3
              ? item?.withoutWorkers?.slice(0, 3)
              : item?.withoutWorkers;
          const noWorkersInstances = showAllNoWorkerInstances
            ? item?.withoutWorkers
            : itemsWithoutWorkers;
          const onShowAllInstancesWithoutWorkersClick = () => {
            setShowAllNoWorkerInstances(prev => !prev);
          };

          return (
            <div key={`${item.appName}-${index}`}>
              <S.AppName size="small" weight="semiBold">
                {item.appName}{" "}
                <S.AppNameNumber>({appItemsLength})</S.AppNameNumber>
              </S.AppName>
              <Accordion.Root type="multiple" defaultValue={["item-0"]}>
                {item?.withoutWorkers && (
                  <Accordion.Item value={`item-${index}`}>
                    <S.Line>
                      <hr />
                    </S.Line>
                    <S.AccordionHeader>
                      <S.Title>
                        <S.AppNameWrapper>
                          <Tooltip
                            tooltip={intl.formatMessage({
                              id: "app"
                            })}
                            side="top"
                            analyticId="app"
                          >
                            <App />
                          </Tooltip>
                          <S.AppNameWorkers weight="semiBold">
                            <Highlightable
                              searchText={searchTitle}
                              textToHighlight={item.appName ? item.appName : ""}
                            />
                            <S.AppNameNumber>
                              {" "}
                              ({item?.withoutWorkers?.length})
                            </S.AppNameNumber>
                          </S.AppNameWorkers>
                        </S.AppNameWrapper>
                      </S.Title>
                      <Accordion.Trigger>
                        <Accordion.Chevron />
                      </Accordion.Trigger>
                    </S.AccordionHeader>
                    <Accordion.Content>
                      {noWorkersInstances?.map((i, idx) => (
                        <div key={`environment-ssh-cmd-div-${idx}`}>
                          <S.Instance weight="semiBold">
                            Instance {i.instance}
                          </S.Instance>
                          <Code.Block
                            singleLine
                            isCopyable
                            id={`environment-ssh-cmd-${idx}`}
                          >
                            {i.cliCommand}
                          </Code.Block>
                        </div>
                      ))}
                      {item?.withoutWorkers?.length > 3 && (
                        <S.ShowAll>
                          <FormattedMessage
                            id="show_all_instances_info"
                            values={{
                              all_instances_number: item?.withoutWorkers.length,
                              shown_instances: showAllNoWorkerInstances
                                ? item?.withoutWorkers.length
                                : itemsWithoutWorkers?.length
                            }}
                          />
                          <S.ShowAllButton
                            onClick={onShowAllInstancesWithoutWorkersClick}
                          >
                            <FormattedMessage
                              id={
                                showAllNoWorkerInstances
                                  ? "hide_all_instances"
                                  : "show_all_instances"
                              }
                            />
                          </S.ShowAllButton>
                        </S.ShowAll>
                      )}
                    </Accordion.Content>
                  </Accordion.Item>
                )}
              </Accordion.Root>
              {item.withWorkers?.map((workers, workersIndex) => {
                const onShowAllInstancesWithWorkersClick = () => {
                  setShowAllWorkerInstances(prev => !prev);
                };
                const firstThreeInstances =
                  workers.instances.length > 3
                    ? workers.instances.slice(0, 3)
                    : workers.instances;
                const workersInstances = showAllWorkerInstances
                  ? workers.instances
                  : firstThreeInstances;
                const includesSearchTitle =
                  searchTitle && workers.workerName.includes(searchTitle);

                return (
                  <>
                    <Accordion.Root
                      type="multiple"
                      key={`environment-ssh-cmd-worker${workersIndex}`}
                      defaultValue={["workers-item-messenger-0"]}
                    >
                      <Accordion.Item
                        value={`workers-item-${workers.workerName}-${workersIndex}`}
                      >
                        {" "}
                        <S.Line>
                          <hr />
                        </S.Line>
                        <S.AccordionHeader>
                          <S.Title>
                            <S.AppNameWrapper>
                              <Tooltip
                                tooltip={intl.formatMessage({
                                  id: "worker"
                                })}
                                side="top"
                                analyticId="worker"
                              >
                                <Worker />
                              </Tooltip>
                              <S.AppNameWorkers weight="semiBold">
                                {includesSearchTitle ? (
                                  <Highlightable
                                    searchText={searchTitle}
                                    textToHighlight={
                                      workers.workerName
                                        ? workers.workerName
                                        : ""
                                    }
                                  />
                                ) : (
                                  workers.workerName
                                )}
                                <S.AppNameNumber>
                                  {" "}
                                  ({workers.instances?.length})
                                </S.AppNameNumber>
                              </S.AppNameWorkers>
                            </S.AppNameWrapper>
                          </S.Title>
                          <Accordion.Trigger>
                            <Accordion.Chevron />
                          </Accordion.Trigger>
                        </S.AccordionHeader>
                        <Accordion.Content>
                          {workers.instances.length > 3 ? (
                            <>
                              {
                                <>
                                  {workersInstances.map(
                                    (instance, instanceIndex) => (
                                      <React.Fragment
                                        key={`environment-cli-cmd-instance-${instanceIndex}`}
                                      >
                                        <S.Instance
                                          weight="semiBold"
                                          key={`environment-ssh-cmd-instance-${instanceIndex}`}
                                        >
                                          Instance {instance.instance}
                                        </S.Instance>
                                        <Code.Block
                                          singleLine
                                          isCopyable
                                          id="cli_cmd"
                                        >
                                          {instance.cliCommand}
                                        </Code.Block>
                                      </React.Fragment>
                                    )
                                  )}
                                  <S.ShowAll>
                                    <FormattedMessage
                                      id="show_all_instances_info"
                                      values={{
                                        all_instances_number:
                                          workers.instances.length,
                                        shown_instances: showAllWorkerInstances
                                          ? workers.instances.length
                                          : workersInstances.length
                                      }}
                                    />
                                    <S.ShowAllButton
                                      onClick={
                                        onShowAllInstancesWithWorkersClick
                                      }
                                    >
                                      <FormattedMessage
                                        id={
                                          showAllWorkerInstances
                                            ? "hide_all_instances"
                                            : "show_all_instances"
                                        }
                                      />
                                    </S.ShowAllButton>
                                  </S.ShowAll>
                                </>
                              }
                            </>
                          ) : (
                            <>
                              {" "}
                              {workers.instances.map(
                                (instance, instanceIndex) => (
                                  <React.Fragment
                                    key={`environment-cli-cmd-instance-${instanceIndex}`}
                                  >
                                    <S.Instance
                                      weight="semiBold"
                                      key={`environment-ssh-cmd-instance-${instanceIndex}`}
                                    >
                                      Instance {instance.instance}
                                    </S.Instance>
                                    <Code.Block
                                      singleLine
                                      isCopyable
                                      id="cli_cmd"
                                    >
                                      {instance.cliCommand}
                                    </Code.Block>
                                  </React.Fragment>
                                )
                              )}
                            </>
                          )}
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion.Root>
                  </>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
};
export default CLISection;
